import { Component, computed, contentChild, input, output, signal } from '@angular/core';
import { FtExpandableSecondaryDirective } from './expandable-slots';

/**
 * Native-Angular port of the AngularJS `ftExpandableContent` directive
 * (account-v2/.../components/expandable-content/expandableContent.directive.ts).
 *
 * Expand/collapse panel used by advocacy/audiences sections. Four named
 * slots — `<collapsed>` (always visible when not expanded), `<primary>`
 * (visible only when expanded), `<custom>` (visible always, no positioning),
 * and `<secondary>`.
 *
 * The `secondary` slot reproduces v1's behaviour: the AngularJS controller
 * rewrote each `<panel>` child into a `.ft-panel` structure and appended them
 * to a generated `.ft-secondary-area` — *only when `<panel>`s existed*. Here
 * `<panel>` is the native `PanelComponent` (host = `.ft-panel`) and this
 * component projects `<ng-content select="secondary">` into a `.ft-secondary-area`
 * wrapper that renders only when a `<secondary>` slot is supplied (queried via
 * `@ContentChild(FtExpandableSecondaryDirective)`), so expandables without a
 * secondary slot don't gain an empty area in the expanded state. The slot tags
 * themselves are made known elements by the marker directives in
 * `expandable-slots.ts`.
 *
 * `onAfterExpanded` / `onAfterCollapsed` are exposed as @Output()s mirroring
 * the v1 `&?` bindings. `isBeingCollapsed` keeps the 2s post-collapse class
 * the v1 controller used for animations.
 */
@Component({
    selector: 'ft-expandable-content',
    templateUrl: './expandable-content.component.html',
    standalone: false
})
export class FtExpandableContentComponent {
    readonly expandableBgImage = input<string>();
    readonly collapsedBgImage = input<string>();

    readonly afterExpanded = output<void>();
    readonly afterCollapsed = output<void>();

    private readonly secondarySlot = contentChild(FtExpandableSecondaryDirective);
    protected readonly hasSecondary = computed(() => !!this.secondarySlot());

    protected readonly isExpanded = signal(false);
    protected readonly isBeingCollapsed = signal(false);

    protected expand(): void {
        this.isExpanded.set(true);
        this.afterExpanded.emit();
    }

    protected collapse(event: Event): void {
        event.stopPropagation();
        this.isExpanded.set(false);
        this.isBeingCollapsed.set(true);
        this.afterCollapsed.emit();
        setTimeout(() => {
            this.isBeingCollapsed.set(false);
        }, 2000);
    }
}
